/**
 * Share-link hooks (`/e/:shareToken/*`).
 *
 * Everything here works with two identities: an anonymous visitor who can read
 * the overview and the recommendations, and a signed-in participant who can
 * additionally join, vote, comment and export the plan.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import type {
  CalendarViewResponse,
  CommentsResponse,
  DepartureOptionsResponse,
  DeparturesResponse,
  JoinEventInput,
  ParticipantResponse,
  PublicEventResponse,
  PublicParticipantsResponse,
  RoundDetailResponse,
  RoundsResponse,
  RunRecommendationResponse,
  VoteInput,
  VoteResultDto,
} from '@shared/types'

import { apiRequest, ApiError } from '../lib/api'
import { queryKeys } from '../lib/query'
import { selectIsAuthenticated, useSessionStore } from '../stores/session'

export function useSharedEvent(shareToken: string | undefined) {

  return useQuery({
    queryKey: queryKeys.sharedEvent(shareToken ?? ''),
    enabled: Boolean(shareToken),
    queryFn: () =>
      apiRequest<PublicEventResponse>(`/api/shared/${shareToken}`),
  })
}

export function useSharedParticipants(shareToken: string | undefined) {
  const isAuthenticated = useSessionStore(selectIsAuthenticated)

  return useQuery({
    queryKey: queryKeys.sharedParticipants(shareToken ?? ''),
    enabled: Boolean(shareToken) && isAuthenticated,
    queryFn: () =>
      apiRequest<PublicParticipantsResponse>(`/api/shared/${shareToken}/participants`),
  })
}

/**
 * The caller's own participation.
 *
 * A 404 is a normal answer (not joined yet), so it is turned into `null` instead
 * of an error the page would have to special case.
 */
export function useMyParticipant(shareToken: string | undefined) {
  const isAuthenticated = useSessionStore(selectIsAuthenticated)

  return useQuery({
    queryKey: queryKeys.myParticipant(shareToken ?? ''),
    enabled: Boolean(shareToken) && isAuthenticated,
    queryFn: async () => {
      try {
        const result = await apiRequest<ParticipantResponse>(
          `/api/shared/${shareToken}/participants/me`,
        )
        return result.participant
      } catch (error) {
        if (error instanceof ApiError && error.status === 404) {
          return null
        }
        throw error
      }
    },
  })
}

export function useJoinEvent(shareToken: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (input: JoinEventInput) =>
      apiRequest<ParticipantResponse>(`/api/shared/${shareToken}/participants`, {
        method: 'POST',
        body: input,
      }),
    onSuccess: () => {
      // Joining changes the participant list, the departure points every other
      // member may travel with, and the pressure warning the overview shows, so
      // all of those answers are stale now.
      void queryClient.invalidateQueries({ queryKey: queryKeys.myParticipant(shareToken) })
      void queryClient.invalidateQueries({ queryKey: queryKeys.sharedEvent(shareToken) })
      void queryClient.invalidateQueries({ queryKey: queryKeys.sharedParticipants(shareToken) })
      void queryClient.invalidateQueries({ queryKey: queryKeys.sharedDepartures(shareToken) })
      void queryClient.invalidateQueries({ queryKey: queryKeys.sharedDepartureOptions(shareToken) })
      void queryClient.invalidateQueries({ queryKey: queryKeys.sharedCalendar(shareToken) })
    },
  })
}

/**
 * Asks for a recommendation round from the share link.
 *
 * Members may trigger a round, not only the organizer, so this is the entry the
 * shared overview offers. The budget is enforced on the server; the client only
 * avoids offering an action that is guaranteed to be refused. `background` asks
 * for the queued path, which the shared page uses once a round has proved slow:
 * the member can then keep reading the page while the work continues server side
 * instead of waiting on one long request.
 */
export function useRunSharedRecommendation(shareToken: string, eventId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (input: { relaxedTravelMinutes?: number; background?: boolean } = {}) =>
      apiRequest<RunRecommendationResponse>(`/api/events/${eventId}/recommendations`, {
        method: 'POST',
        body: input,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.sharedRounds(shareToken) })
      void queryClient.invalidateQueries({ queryKey: queryKeys.sharedEvent(shareToken) })
    },
  })
}

export function useSharedRounds(shareToken: string | undefined) {
  return useQuery({
    queryKey: queryKeys.sharedRounds(shareToken ?? ''),
    enabled: Boolean(shareToken),
    queryFn: () => apiRequest<RoundsResponse>(`/api/shared/${shareToken}/recommendations`),
  })
}

export function useSharedRound(shareToken: string | undefined, round: number | undefined) {
  return useQuery({
    queryKey: queryKeys.sharedRound(shareToken ?? '', round ?? 0),
    enabled: Boolean(shareToken && round),
    queryFn: () =>
      apiRequest<RoundDetailResponse>(`/api/shared/${shareToken}/recommendations/${round}`),
    // A round that is still running is polled so the progress counter advances and
    // the result appears without the reader having to reload. A finished round is
    // read once, because nothing about it can change until somebody runs another.
    refetchInterval: (query) => {
      const status = query.state.data?.round.runStatus
      return status === undefined || status === 'ready' || status === 'failed' ? false : 3000
    },
  })
}

export function useRoundVotes(shareToken: string | undefined, round: number | undefined) {
  const isAuthenticated = useSessionStore(selectIsAuthenticated)

  return useQuery({
    queryKey: queryKeys.sharedVotes(shareToken ?? '', round ?? 0),
    enabled: Boolean(shareToken) && isAuthenticated && Boolean(round),
    queryFn: () =>
      apiRequest<VoteResultDto>(`/api/shared/${shareToken}/rounds/${round}/votes`),
  })
}

export function useSubmitVote(shareToken: string, round: number) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (input: VoteInput) =>
      apiRequest<VoteResultDto>(`/api/shared/${shareToken}/rounds/${round}/votes`, {
        method: 'POST',
        body: input,
      }),
    onSuccess: () => {
      // The tally the round detail shows includes this ballot, so the round is
      // stale as well as the vote result itself.
      void queryClient.invalidateQueries({ queryKey: queryKeys.sharedVotes(shareToken, round) })
      void queryClient.invalidateQueries({ queryKey: queryKeys.sharedRound(shareToken, round) })
      void queryClient.invalidateQueries({ queryKey: queryKeys.sharedRounds(shareToken) })
    },
  })
}

/**
 * The discussion thread.
 *
 * The first read asks for the newest page, so a thread longer than one page opens
 * where the conversation is; every later poll passes the cursor the previous answer
 * returned, which is what lets messages written after it arrive at all.
 */
export function useComments(
  shareToken: string | undefined,
  options: { after?: number | null; tail?: boolean } = {},
) {
  const isAuthenticated = useSessionStore(selectIsAuthenticated)
  const { after = null, tail = false } = options

  return useQuery({
    queryKey: [...queryKeys.sharedComments(shareToken ?? ''), after ?? 0, tail ? 'tail' : 'next'],
    enabled: Boolean(shareToken) && isAuthenticated,
    refetchInterval: 5000,
    queryFn: () => {
      const params = new URLSearchParams()
      if (after !== null) {
        params.set('after', String(after))
      }
      if (tail) {
        params.set('tail', '1')
      }
      const query = params.toString()
      return apiRequest<CommentsResponse>(
        `/api/shared/${shareToken}/comments${query === '' ? '' : `?${query}`}`,
      )
    },
  })
}

export function usePostComment(shareToken: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (input: { content: string; replyToId?: string | null }) =>
      apiRequest(`/api/shared/${shareToken}/comments`, { method: 'POST', body: input }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.sharedComments(shareToken) })
    },
  })
}

export function useCalendarView(shareToken: string | undefined) {

  return useQuery({
    queryKey: queryKeys.sharedCalendar(shareToken ?? ''),
    enabled: Boolean(shareToken),
    queryFn: () => apiRequest<CalendarViewResponse>(`/api/shared/${shareToken}/calendar`),
  })
}

/**
 * Departure points of an event.
 *
 * Members only, because a departure point can be a private address a member
 * chose, so the section that shows them is rendered for the organizer and for
 * joined participants rather than for every visitor with the link.
 */
export function useDepartures(shareToken: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.sharedDepartures(shareToken ?? ''),
    enabled: Boolean(shareToken) && enabled,
    queryFn: () => apiRequest<DeparturesResponse>(`/api/shared/${shareToken}/departures`),
  })
}

/**
 * Departure points a joining member may travel with.
 *
 * Available before membership exists, because the wizard asks the question while
 * registering. Only names and sizes are returned, never an address.
 */
export function useDepartureOptions(shareToken: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.sharedDepartureOptions(shareToken ?? ''),
    enabled: Boolean(shareToken) && enabled,
    queryFn: () => apiRequest<DepartureOptionsResponse>(`/api/shared/${shareToken}/departures/options`),
  })
}

/** Dismisses the ceiling warning for the caller, once. */export function useDismissDepartureWarning(shareToken: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () =>
      apiRequest<{ ok: boolean }>(`/api/shared/${shareToken}/departures/warning`, {
        method: 'POST',
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.sharedDepartures(shareToken) })
    },
  })
}
